/* global */
/*
 * W2 (2026-06-03) — Cart shipping simulator.
 *
 * Faz fetch dos shipping rates do Shopify endpoint nativo a partir do CEP
 * digitado pelo cliente dentro do cart drawer. Reduz fricção mobile que
 * causa abandono no carrinho (cliente avança sem saber o frete e desiste
 * no checkout).
 *
 * Endpoint flow:
 *   1. POST /cart/prepare_shipping_rates.json
 *      body: shipping_address[zip]=01310-100&shipping_address[country]=BR
 *   2. GET /cart/async_shipping_rates.json (poll a cada 500ms até retornar)
 *      response: { shipping_rates: [{name, price, delivery_days, source}, ...] }
 *
 * Persiste o último CEP em localStorage (key 'cart-shipping-zip') pra
 * re-popular em sessões futuras. Listener em cart:refresh invalida o
 * resultado pra forçar recalc quando o carrinho muda.
 */
(function () {
  if (window.__cartShippingSimulator) return;

  var STORAGE_KEY = 'cart-shipping-zip';
  var POLL_INTERVAL = 500;
  var POLL_MAX_ATTEMPTS = 20;

  function formatCEP(value) {
    var digits = (value || '').replace(/\D/g, '').slice(0, 8);
    if (digits.length > 5) {
      return digits.slice(0, 5) + '-' + digits.slice(5);
    }
    return digits;
  }

  function isValidCEP(value) {
    return /^\d{5}-?\d{3}$/.test(value || '');
  }

  /**
   * Deriva código do estado (UF) a partir dos 5 primeiros dígitos do CEP
   * brasileiro. Necessário pq carriers tipo Frenet exigem `province` no POST
   * /cart/prepare_shipping_rates.json — sem province retornam 422 ou rates
   * vazias/cacheadas de outra região. Ranges oficiais Correios.
   * @param {string} cep
   * @returns {string|null} 2-letter UF code ou null se fora de range
   */
  function cepToProvince(cep) {
    var n = parseInt((cep || '').replace(/\D/g, '').slice(0, 5), 10);
    if (!(n >= 1000 && n <= 99999)) return null;
    if (n <= 19999) return 'SP';
    if (n <= 28999) return 'RJ';
    if (n <= 29999) return 'ES';
    if (n <= 39999) return 'MG';
    if (n <= 48999) return 'BA';
    if (n <= 49999) return 'SE';
    if (n <= 56999) return 'PE';
    if (n <= 57999) return 'AL';
    if (n <= 58999) return 'PB';
    if (n <= 59999) return 'RN';
    if (n <= 63999) return 'CE';
    if (n <= 64999) return 'PI';
    if (n <= 65999) return 'MA';
    if (n <= 68899) return 'PA';
    if (n <= 68999) return 'AP';
    if (n <= 69299) return 'AM';
    if (n <= 69399) return 'RR';
    if (n <= 69899) return 'AM';
    if (n <= 69999) return 'AC';
    if (n <= 72799) return 'DF';
    if (n <= 72999) return 'GO';
    if (n <= 73699) return 'DF';
    if (n <= 76799) return 'GO';
    if (n <= 76999) return 'RO';
    if (n <= 77999) return 'TO';
    if (n <= 78899) return 'MT';
    if (n <= 78999) return 'RO';
    if (n <= 79999) return 'MS';
    if (n <= 87999) return 'PR';
    if (n <= 89999) return 'SC';
    if (n <= 99999) return 'RS';
    return null;
  }

  function formatBRL(value) {
    var num = parseFloat(value);
    if (!isFinite(num)) return '';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2
    }).format(num);
  }

  function formatDeliveryDays(days) {
    if (!days || days < 1) return '';
    if (days === 1) return '1 dia útil';
    return days + ' dias úteis';
  }

  function setState(root, state) {
    if (!root) return;
    root.setAttribute('data-state', state);
  }

  function setStatus(root, message) {
    var el = root.querySelector('[data-cart-shipping-status]');
    if (!el) return;
    if (message) {
      el.textContent = message;
      el.hidden = false;
    } else {
      el.textContent = '';
      el.hidden = true;
    }
  }

  function renderRates(root, rates) {
    var list = root.querySelector('[data-cart-shipping-rates]');
    if (!list) return;
    list.innerHTML = '';
    if (!rates || !rates.length) {
      list.hidden = true;
      return;
    }
    rates.forEach(function (rate) {
      var li = document.createElement('li');
      li.className = 'cart-shipping-simulator__rate';

      var name = document.createElement('span');
      name.className = 'cart-shipping-simulator__rate-name';
      name.textContent = rate.name || rate.presentment_name || 'Entrega';

      var meta = document.createElement('span');
      meta.className = 'cart-shipping-simulator__rate-meta';
      var days = flattenDays(rate.delivery_days != null ? rate.delivery_days : rate.estimated_delivery_days);
      var deliveryText = formatDeliveryDays(days);
      meta.textContent = deliveryText;

      var price = document.createElement('strong');
      price.className = 'cart-shipping-simulator__rate-price';
      var priceValue = rate.price;
      if (priceValue === 0 || priceValue === '0.00' || priceValue === '0') {
        price.textContent = 'Grátis';
        price.classList.add('cart-shipping-simulator__rate-price--free');
      } else {
        price.textContent = formatBRL(priceValue);
      }

      li.appendChild(name);
      if (deliveryText) li.appendChild(meta);
      li.appendChild(price);
      list.appendChild(li);
    });
    list.hidden = false;
  }

  function pollRates(attempts) {
    attempts = attempts || 0;
    if (attempts >= POLL_MAX_ATTEMPTS) {
      return Promise.reject(new Error('timeout'));
    }
    return fetch('/cart/async_shipping_rates.json', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data && data.shipping_rates) {
          return data.shipping_rates;
        }
        return new Promise(function (resolve, reject) {
          setTimeout(function () {
            pollRates(attempts + 1).then(resolve, reject);
          }, POLL_INTERVAL);
        });
      });
  }

  function fetchRates(zip) {
    var body = new URLSearchParams();
    body.set('shipping_address[zip]', zip);
    body.set('shipping_address[country]', 'BR');
    var province = cepToProvince(zip);
    if (province) {
      body.set('shipping_address[province]', province);
    }
    return fetch('/cart/prepare_shipping_rates.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        'Accept': 'application/json'
      },
      body: body.toString(),
      credentials: 'same-origin'
    })
      .then(function (res) {
        // Shopify retorna 422 quando o carrier (ex: Frenet) exige campos
        // adicionais como province. Mesmo assim, async_shipping_rates pode
        // devolver rates válidas (cache de session). Só abortamos em 5xx.
        // Guardamos a msg de validação pra exibir SE o async também vier vazio.
        if (res.status >= 500) {
          throw new Error('prepare-failed');
        }
        if (res.status === 422) {
          return res.json().then(function (data) {
            var msg = (data && (data.message || (data.zip && data.zip[0]) || (data.province && data.province[0]))) || '';
            return pollRates(0).then(function (rates) {
              // Se async retornou rates, usar. Se vazio, propagar msg.
              if (rates && rates.length) return rates;
              if (msg && /cep|zip|inv/i.test(msg)) throw new Error(msg);
              return rates; // [] vira state 'empty'
            });
          });
        }
        return pollRates(0);
      });
  }

  function flattenDays(d) {
    if (Array.isArray(d)) {
      // Shopify às vezes retorna [min, max]. Usamos o maior pra ser conservador.
      var max = Math.max.apply(null, d.filter(function (v) { return typeof v === 'number'; }));
      return isFinite(max) ? max : null;
    }
    if (typeof d === 'number') return d;
    return null;
  }

  function handleCalculate(root) {
    var input = root.querySelector('[data-cart-shipping-input]');
    if (!input) return;
    var raw = input.value || '';
    if (!isValidCEP(raw)) {
      setState(root, 'error');
      setStatus(root, 'Digite um CEP válido (00000-000).');
      renderRates(root, []);
      return;
    }
    var zip = formatCEP(raw);
    input.value = zip;
    try { localStorage.setItem(STORAGE_KEY, zip); } catch (e) {}

    setState(root, 'loading');
    setStatus(root, 'Calculando frete…');
    renderRates(root, []);

    fetchRates(zip)
      .then(function (rates) {
        if (!rates || !rates.length) {
          setState(root, 'empty');
          setStatus(root, 'Não entregamos para este CEP no momento.');
          return;
        }
        setState(root, 'success');
        setStatus(root, '');
        renderRates(root, rates);
      })
      .catch(function (err) {
        setState(root, 'error');
        var msg = (err && err.message) || 'Erro ao consultar';
        if (msg === 'timeout') msg = 'Demorou demais. Tente novamente.';
        if (msg === 'prepare-failed') msg = 'Erro ao consultar. Tente novamente.';
        setStatus(root, msg);
      });
  }

  function bindInputFormat(input) {
    input.addEventListener('input', function () {
      var pos = input.selectionStart;
      var oldLen = input.value.length;
      input.value = formatCEP(input.value);
      var newLen = input.value.length;
      var delta = newLen - oldLen;
      if (pos != null) {
        input.setSelectionRange(pos + delta, pos + delta);
      }
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        var root = input.closest('[data-cart-shipping-simulator]');
        if (root) handleCalculate(root);
      }
    });
  }

  function init(root) {
    if (!root || root.__init) return;
    root.__init = true;

    var input = root.querySelector('[data-cart-shipping-input]');
    var btn = root.querySelector('[data-cart-shipping-trigger]');

    if (input) {
      bindInputFormat(input);
      // Restore last used CEP
      try {
        var saved = localStorage.getItem(STORAGE_KEY);
        if (saved && isValidCEP(saved)) {
          input.value = formatCEP(saved);
        }
      } catch (e) {}
    }

    if (btn) {
      btn.addEventListener('click', function () { handleCalculate(root); });
    }
  }

  function invalidateAll() {
    document.querySelectorAll('[data-cart-shipping-simulator]').forEach(function (root) {
      // Cart mudou; rates ficaram stale. Reset UI mas preserva o CEP digitado.
      setState(root, 'idle');
      setStatus(root, '');
      renderRates(root, []);
    });
  }

  function initAll() {
    document.querySelectorAll('[data-cart-shipping-simulator]').forEach(init);
  }

  document.addEventListener('DOMContentLoaded', initAll);
  if (document.readyState !== 'loading') initAll();

  // Quando o cart drawer abre, garante que o simulator (que pode ter sido
  // adicionado dinamicamente pelo theme) está inicializado.
  document.addEventListener('cart-drawer:open', initAll);
  document.addEventListener('cart:open', initAll);

  // Invalida rates quando cart muda (qty change, add/remove item).
  document.addEventListener('cart:refresh', invalidateAll);
  document.addEventListener('cart:updated', invalidateAll);
  document.addEventListener('cart:change', invalidateAll);

  // ── Adiciona/remove classe body.cart-drawer-open + esconde floating overlays
  // AGGRESSIVE MODE: detecta QUALQUER element fixed/absolute/sticky pequeno
  // visível em qualquer canto, exceto os que fazem parte do drawer/overlay/header.
  // Resolve casos onde WhatsApp/chat buttons cobrem o CTA do carrinho.
  var hiddenFloatings = [];
  function isPartOfCartDrawer(el) {
    return !!(el.closest('cart-drawer')
      || el.closest('.cart-drawer')
      || el.closest('.cart-drawer__footer')
      || el.closest('[data-cart-drawer]')
      || el.closest('.js-cart-drawer')
      || el.classList.contains('overlay')
      || el.id === 'siteBackdrop'
      || el.classList.contains('cart-drawer-overlay'));
  }
  function hideFloatingChatButtons() {
    var vh = window.innerHeight;
    var vw = window.innerWidth;
    document.body.querySelectorAll('*').forEach(function (el) {
      if (isPartOfCartDrawer(el)) return;
      if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE' || el.tagName === 'HEAD') return;
      if (hiddenFloatings.indexOf(el) !== -1) return;
      try {
        var cs = getComputedStyle(el);
        // Considera fixed E sticky E absolute (todos podem flutuar)
        if (cs.position !== 'fixed' && cs.position !== 'sticky') return;
        if (cs.display === 'none' || cs.visibility === 'hidden') return;
        var r = el.getBoundingClientRect();
        if (r.width <= 0 || r.height <= 0) return;
        // Pula elementos full-viewport (overlay, dialog, modal, header)
        var isFullWidth = r.width >= vw - 20;
        var isTallEnoughForLayout = r.height >= vh * 0.5;
        if (isFullWidth && isTallEnoughForLayout) return;
        // Pula headers fixos no topo
        if (r.top < 100 && r.height < 120 && isFullWidth) return;
        // Heurística amplificada: qualquer floating element não-full-screen.
        // Range expandido pra cobrir "WhatsApp Button" (que pode ser maior).
        var isFloatSize = r.width > 16 && r.width < 320 && r.height > 16 && r.height < 320;
        if (!isFloatSize) return;
        // Sanidade: tem que estar perto de uma borda (canto inferior/lateral)
        var nearBottom = (vh - r.bottom) < 200;
        var nearRight = (vw - r.right) < 200;
        var nearLeft = r.left < 200;
        if (!nearBottom && !nearRight && !nearLeft) return;
        // Esconde
        el.dataset.cartDrawerHidden = '1';
        el.style.setProperty('display', 'none', 'important');
        el.style.setProperty('visibility', 'hidden', 'important');
        el.style.setProperty('pointer-events', 'none', 'important');
        hiddenFloatings.push(el);
      } catch (e) { /* silent */ }
    });
  }
  function restoreFloatingChatButtons() {
    hiddenFloatings.forEach(function (el) {
      if (el && el.dataset && el.dataset.cartDrawerHidden === '1') {
        el.style.removeProperty('display');
        el.style.removeProperty('visibility');
        el.style.removeProperty('pointer-events');
        delete el.dataset.cartDrawerHidden;
      }
    });
    hiddenFloatings = [];
  }
  function syncBodyCartDrawerClass() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    var isOpen = drawer.getAttribute('aria-hidden') === 'false'
      || drawer.classList.contains('drawer--open')
      || drawer.classList.contains('is-open')
      || drawer.classList.contains('open')
      || document.body.classList.contains('cart-open')
      || document.documentElement.classList.contains('cart-drawer-open');
    var wasOpen = document.body.classList.contains('cart-drawer-open');
    document.body.classList.toggle('cart-drawer-open', isOpen);
    if (isOpen && !wasOpen) {
      // Drawer recém-aberto — esconde chat buttons (delay pra DOM settle)
      setTimeout(hideFloatingChatButtons, 50);
      setTimeout(hideFloatingChatButtons, 300); // 2nd pass pra lazy-injected
      setTimeout(hideFloatingChatButtons, 1000); // 3rd pass pra apps lentos
    } else if (!isOpen && wasOpen) {
      restoreFloatingChatButtons();
    }
  }
  // Hook secundário: também escuta cliques no cart icon (caso o aria-hidden
  // não seja atualizado — algumas implementações só toggle class)
  function attachClickFallback() {
    var cartLinks = document.querySelectorAll('[data-modal-trigger="cart"], .cart-link, .header__cart, a[href="/cart"]');
    cartLinks.forEach(function (link) {
      if (link.__cartHideAttached) return;
      link.__cartHideAttached = true;
      link.addEventListener('click', function () {
        setTimeout(syncBodyCartDrawerClass, 100);
        setTimeout(syncBodyCartDrawerClass, 500);
      });
    });
  }
  document.addEventListener('DOMContentLoaded', attachClickFallback);
  if (document.readyState !== 'loading') attachClickFallback();
  // MutationObserver pra detectar mudança no aria-hidden ou class do <cart-drawer>
  function attachDrawerObserver() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.__observed) return;
    drawer.__observed = true;
    var obs = new MutationObserver(syncBodyCartDrawerClass);
    obs.observe(drawer, { attributes: true, attributeFilter: ['aria-hidden', 'class'] });
    syncBodyCartDrawerClass();
  }
  document.addEventListener('DOMContentLoaded', attachDrawerObserver);
  if (document.readyState !== 'loading') attachDrawerObserver();

  // ── Quando um toggle (shipping ou cupom) abre, fecha o outro ─────────
  // Evita 2 panels expandidos ao mesmo tempo (que ficaria confuso visualmente).
  function attachToggleAccordion() {
    var row = document.querySelector('.cart-toggles-row');
    if (!row || row.__accordion) return;
    row.__accordion = true;
    row.querySelectorAll('details').forEach(function (d) {
      d.addEventListener('toggle', function () {
        if (d.open) {
          row.querySelectorAll('details').forEach(function (other) {
            if (other !== d) other.open = false;
          });
        }
      });
    });
  }
  document.addEventListener('DOMContentLoaded', attachToggleAccordion);
  document.addEventListener('cart-drawer:open', attachToggleAccordion);
  document.addEventListener('cart:open', attachToggleAccordion);
  document.addEventListener('cart:refresh', attachToggleAccordion);
  if (document.readyState !== 'loading') attachToggleAccordion();

  window.__cartShippingSimulator = { init: init, initAll: initAll, invalidateAll: invalidateAll };
})();
